import { useEffect, useState, useMemo, useCallback } from 'react';
import { useApplicationContext } from '../ApplicationContext';
import {
    AiPromptForm,
    SubscriptionPromotionModal,
    GetSubscriptionModal,
} from '~/components';
import { PromptsHistoryList } from '~/components/CardsGridView';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { prompts } from '~/services';
import { ScrollArea, ScrollBar } from '~/components/ui/scroll-area';
import { Form as AntdForm, Drawer, Input, Menu, QRCode, Skeleton } from 'antd';
import {
    CircleDollarSign,
    LifeBuoy,
    MailOpen,
    Unplug,
    ArrowDownToLine,
    Copy,
    BookOpenText,
    ExternalLink,
    CircleHelp,
    Share,
    Settings2,
    History as HistoryLogo,
    FolderOpen,
    ArrowDownZA,
    ChartNoAxesCombined,
    Users,
    Gem,
    Globe,
    ArrowUpRight,
} from 'lucide-react';
import { getLanguagesList } from '~/services/languages';
import {
    History,
    WandSparkles,
    BotMessageSquare,
    CircleUserRound,
} from 'lucide-react';

import SignInModal, { useSignInModal } from '../SiginModal';
import {
    useDisplayTokens,
    useGetPromptsTemplates,
    useMessagesAPI,
    useSetMessagesAPI,
} from '~/hooks';
import { message, Select } from 'antd';
import ActivateSubscriptionModal, {
    useActivateSubscriptionDialog,
} from '~/components/ActivateSusbcriptionModal';
import { Link, useFetcher, useSearchParams } from '@remix-run/react';

import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { Placeholder } from './components/placeholder.client';

import {
    AiPromptModalBody,
    AiResultForm,
    defaultFormValues,
    LoadingPlaceholder,
    useAiPromptForm,
} from '../AiPromptForm/index.client';
import { ChatWidget } from '../ChatWidget';
import { useSubscriptionPromotionModal } from '../SubscriptionPromotionModal';
import { Button } from '../ui/button';
import { Input as ShadcnInput } from '../ui/input';
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableRow,
} from '~/components/ui/table';
import PowerPointLogo from '~/assets/powerpoint.svg';
// import ExcelLogo from '~/assets/excel.svg';
import WordLogo from '~/assets/word.svg';
import OutlookLogo from '~/assets/outlook.svg';
import TeamsLogo from '~/assets/teams.svg';
import ChromeLogo from '~/assets/chrome.svg';
import GmailLogo from '~/assets/gmail.svg';
import FlamePng from '~/assets/flame.png';
import WindowsMarketplaceLogo from '~/assets/win_store.svg';
import TelegramLogo from '~/assets/telegram.svg';
import AppleLogo from '~/assets/apple-logo.svg';
import AndroidLogo from '~/assets/android-logo.svg';
import OneNote from '~/assets/onenote.svg';
import ExcelLogo from '~/assets/excel.svg';
import SheetsLogo from '~/assets/sheets.svg';
import DocsLogo from '~/assets/docs.svg';
import SlidesLogo from '~/assets/slides.svg';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTrigger,
} from '~/components/ui/dialog';
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from '~/components/ui/hover-card';

import { useNavigate } from '@remix-run/react';

const defaultSegment = atomWithStorage('defaultSegment', 'request', undefined, {
    getOnInit: true,
});

const defaultPromptId = atomWithStorage('defaultTemplateId', null, undefined, {
    getOnInit: true,
});

function filterPromptsTemplates(query: string, template: any): boolean {
    if (!query || !template) {
        return true;
    }

    const normalizedInputValue = query.toLowerCase().trim();

    if (!normalizedInputValue) {
        return true;
    }

    const sources = [
        template.title,
        template.description,
        template.textPlaceholder,
        template.seoTitle,
        template.seoDescription,
        template.type,
    ]
        .filter((i) => !!i)
        .map((i) => i.toLowerCase().trim());

    if (
        query &&
        !sources.some((source) => source.includes(query.toLowerCase()))
    ) {
        return false;
    }

    return true;
}

function OutlookView({
    promptId,
    buttonLabel,
    text,
    onChangeView,
}: {
    promptId: string;
    buttonLabel: string;
    text: string;
    onChangeView: () => void;
}) {
    const USER_INPUT_STATE = 'USER_INPUT_STATE';
    const AI_RESULT_STATE = 'AI_RESULT_STATE';
    const FETCHING = 'FETCHING';

    const [form] = AntdForm.useForm();
    const [formState, setFormState] = useState(USER_INPUT_STATE);
    const [initialFormValues, setInitialFormValues] =
        useAtom(defaultFormValues);

    const initialValues = {
        ...initialFormValues,
        text: text,
    };

    const debouncedUpdateInitialFormFields = useDebouncedCallback(
        // function
        () => {
            const values = form.getFieldsValue();
            setInitialFormValues({
                ...initialFormValues,
                ...values,
                text: null,
            });
        },
        // delay in ms
        300,
    );

    const applicationContext = useApplicationContext()!;
    const account = applicationContext?.account;
    const { tokens, decreaseTokens } = useDisplayTokens();
    const { openDialog: openSignInDialog } = useSignInModal();
    const { openDialog: openSubscriptionPromotionDialog } =
        useSubscriptionPromotionModal();

    const handleFormSubmit = useCallback(
        async (values: any) => {
            if (!account) {
                openSignInDialog();
                return;
            }

            if (!account?.isUnlimited && tokens <= 0) {
                openSubscriptionPromotionDialog();
                return;
            }

            const prompt = prompts.find((p) => p.id === promptId);

            setFormState(FETCHING);
            const { text } = await getMailItem();
            const payload = JSON.stringify({ ...values, text, id: prompt!.id });
            const res = await fetch('/ai', {
                method: 'POST',
                body: payload,
            });
            const js = await res.json();

            form.setFieldValue('result', js.responses);
            setFormState(AI_RESULT_STATE);

            decreaseTokens();
        },
        [setFormState, form],
    );

    function handleGoBack() {
        setFormState(USER_INPUT_STATE);
    }

    function renderCurrentState() {
        switch (formState) {
            case USER_INPUT_STATE:
                return (
                    <div className='w-full h-full flex flex-col items-center justify-center'>
                        <AiButton>{buttonLabel}</AiButton>
                        <AntdForm.Item key={`item-text`} name={'text'}>
                            <input type='hidden' />
                        </AntdForm.Item>
                        <AntdForm.Item
                            key={`item-tone`}
                            className='!mb-0 w-36'
                            label={
                                <span className='text-muted-foreground text-xs'>
                                    Tone
                                </span>
                            }
                            name='tone'>
                            <Select
                                size='small'
                                className='flex-grow'
                                options={[
                                    {
                                        value: 'professional',
                                        label: '💼 Professional',
                                    },
                                    { value: 'casual', label: '🙂 Casual' },
                                    { value: 'urgent', label: '⏰ Urgent' },
                                    {
                                        value: 'appreciative',
                                        label: '💕 Appreciative',
                                    },
                                    { value: 'funny', label: '😄 Funny' },
                                ]}
                            />
                        </AntdForm.Item>
                        <AntdForm.Item
                            key={`item-language`}
                            className='!mb-0 w-36'
                            label={
                                <span className='text-muted-foreground text-xs'>
                                    Language
                                </span>
                            }
                            name='language'>
                            <Select
                                size='small'
                                showSearch
                                options={getLanguagesList()}
                            />
                        </AntdForm.Item>
                        <Button
                            size='sm'
                            className='font-normal'
                            variant={'link'}
                            onClick={onChangeView}>
                            Show advanced options...
                        </Button>
                    </div>
                );
            case FETCHING:
                return <LoadingPlaceholder />;
            case AI_RESULT_STATE:
                return (
                    <AiResultForm
                        result={form.getFieldValue('result')}
                        isComposeBox={false}
                        handleGoBack={handleGoBack}
                    />
                );
            default:
                throw new Error(`Unknown state ${formState}`);
        }
    }

    return (
        <div className='w-full h-full flex flex-col items-center'>
            <AntdForm
                form={form}
                name='outlook'
                layout='vertical'
                className='flex flex-col pb-8'
                style={{ width: '100%', height: '100%' }}
                initialValues={initialValues}
                autoComplete='off'
                onFieldsChange={(fields, allFields) => {
                    if (!fields.some((f) => f.touched)) {
                        return;
                    }
                    debouncedUpdateInitialFormFields();
                }}
                onFinish={handleFormSubmit}>
                {renderCurrentState()}
            </AntdForm>
        </div>
    );
}

async function getMailItem(): Promise<{ mode: string; text: string }> {
    const promise = new Promise((resolve, reject) => {
        const item = Office.context.mailbox.item;

        if (item?.itemType !== 'message') {
            reject({ message: `item type is not message. ${item?.itemType}` });
            return;
        }

        const mode =
            Office.context.mailbox.item.displayReplyForm != undefined
                ? 'read'
                : 'compose';

        const body = Office.context.mailbox.item?.body;

        body?.getAsync(Office.CoercionType.Text, function (asyncResult) {
            if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                reject(asyncResult);
            } else {
                const subject = Office.context?.mailbox.item?.normalizedSubject;
                const ctx = Office.context?.mailbox.item;
                const text = asyncResult.value;

                const to = Office.context?.mailbox.item?.to?.getAsync
                    ? null
                    : Office.context?.mailbox.item?.to;

                const from = Office.context?.mailbox.item?.from?.getAsync
                    ? null
                    : Office.context?.mailbox.item?.from;

                const chunks = [
                    { label: 'From: ', value: from?.displayName },
                    {
                        label: 'To: ',
                        value: Array.isArray(to)
                            ? to.map((t) => t.displayName).join(',')
                            : null,
                    },
                    { label: 'Subject: ', value: subject },
                    { label: 'Body: ', value: text },
                ]
                    .filter((c) => c.value)
                    .map((c) => `${c.label}${c.value}`)
                    .join(`\n`);

                resolve({ text: chunks, mode });
            }
        });
    });

    return promise;
}

function OutlookRequest({ onChangeView }: { onChangeView: () => void }) {
    const [mode, setMode] = useState<string>('');
    const [text, setText] = useState<string>('');

    useEffect(() => {
        function subscribeToMailBoxEvent() {
            Office.context.mailbox?.addHandlerAsync(
                Office.EventType.ItemChanged,
                async () => {
                    try {
                        const { mode, text } = await getMailItem();
                        setMode(mode);
                        setText(text);
                    } catch (err) {
                        console.error(err);
                    }
                },
            );
        }

        async function init() {
            try {
                const { mode, text } = await getMailItem();
                setMode(mode);
                setText(text);
                subscribeToMailBoxEvent();
            } catch (error) {
                console.error(error);
                onChangeView();
            }
        }

        init();
    }, [setMode, setText]);

    if (!text) {
        return (
            <div className='w-full h-full flex items-center justify-center'>
                <IconRipple icon={MailOpen} />
            </div>
        );
    }

    if (mode === 'read') {
        return (
            <OutlookView
                promptId='mail-reply'
                buttonLabel='Generate Reply'
                text={text}
                onChangeView={onChangeView}
            />
        );
    }

    return (
        <OutlookView
            promptId='mail-improve-draft'
            buttonLabel='Improve Draft'
            text={text}
            onChangeView={onChangeView}
        />
    );
}

function CategoriesPrompts({ onSelect }) {
    const promptsTemplates = useGetPromptsTemplates();

    const templatesToMenuOptions = (templates: string[]) => {
        return templates
            .map((t) => {
                const promptTemplate = promptsTemplates.find(
                    (pt) => pt.id === t,
                );
                return promptTemplate;
            })
            .filter((t) => !!t)
            .map((t) => {
                return {
                    key: t.id,
                    label: `${t.emoji} ${t.title}`,
                    onClick: () => {
                        onSelect(t.id);
                    },
                };
            });
    };

    const [category, setCategory] = useState<any>(null);
    const wordCategory = {
        imgUrl: WordLogo,
        className: `bg-gradient-to-br from-[#3a7bd5] to-[#00d2ff] hover:shadow-[0_0_15px_rgba(0,210,255,0.7)]`,
        name: 'word',
        templates: [
            'fix-grammar',
            `improve`,
            `write-paragraph`,
            'write-resume',
            'summarize',
            'ocr-text',
        ],
    };
    const excelCategory = {
        imgUrl: ExcelLogo,
        className: `bg-gradient-to-br from-[#217346] to-[#3fa84f] hover:shadow-[0_0_15px_rgba(63,168,79,0.7)] `,
        name: 'excel',
        templates: ['generate-excel-formula', `explain-excel-formula`],
    };

    const powerpointCategory = {
        imgUrl: PowerPointLogo,
        className: `bg-gradient-to-br from-[#b7472a] to-[#d96c28]  hover:shadow-[0_0_15px_rgba(217,108,40,0.7)] `,
        name: 'Power Point',
        templates: ['slides-outline', `text-to-slide`, 'ocr-text'],
    };

    const outlookCategory = {
        imgUrl: OutlookLogo,
        className: `bg-gradient-to-br from-[#0072c6] to-[#00a4ef] hover:shadow-[0_0_15px_rgba(0,164,239,0.7)]  `,
        name: 'Outlook',
        templates: [
            'mail-improve-draft',
            `mail-reply`,
            'positive-reply-customer-email',
            'negative-reply-customer-email',
            'angry-customer-email',
            'mail-subject-line-creator',
        ],
    };

    const newCategory = {
        imgUrl: FlamePng,
        className: `bg-gradient-to-br from-black via-[#1a0f00] to-[#ff8c00] hover:shadow-[0_0_20px_5px_#ff8c00] `,
        name: 'New',
        templates: [
            'ocr-text',
            'humanize-text',
            'meeting-minutes-generator',
            'cta-generator',
            'brainstorm-tool',
            'okr-generator',
            'speech-writer',
        ],
    };

    const categories = [
        newCategory,
        wordCategory,
        outlookCategory,
        powerpointCategory,
        excelCategory,
    ];

    return (
        <>
            <div className='flex text-muted-foreground items-center gap-x-2'>
                <FolderOpen className='w-4 h-4' />
                <span
                    className='hover:underline cursor-pointer'
                    onClick={() => setCategory(null)}>
                    Categories
                </span>
                {category && (
                    <>
                        <span>/</span>
                        <span className='capitalize'>{category.name}</span>
                    </>
                )}
            </div>
            {category ? (
                <Menu
                    selectable={false}
                    mode='inline'
                    items={templatesToMenuOptions(category.templates)}
                />
            ) : (
                <div className='py-2 overflow-x-auto'>
                    <div className='flex flex-row gap-2 w-max'>
                        {categories.map((cat) => (
                            <div
                                onClick={() => setCategory(cat)}
                                className={`!flex items-center justify-center w-[75px] h-[75px] rounded-2xl transition-shadow cursor-pointer ${cat.className}`}>
                                <img
                                    src={cat.imgUrl}
                                    className='w-[48px] h-[48px]'
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

function RecentlyUsedPrompts({ onSelect }) {
    const [isFetching, setFetching] = useState(false);
    const [prompts, setPrompts] = useState([]);
    const promptsTemplates = useGetPromptsTemplates();

    useEffect(() => {
        const fetchPrompts = async () => {
            const res = await fetch('/prompts-history', {
                method: 'POST',
            });

            if (res.status !== 200) {
                return { prompts: [] };
            }

            const response = await res.json();
            return response;
        };

        const init = async () => {
            setFetching(true);
            const { prompts } = await fetchPrompts();
            setPrompts(prompts);
            setFetching(false);
        };

        init();
    }, [setFetching, setPrompts]);

    if (isFetching) {
        return (
            <>
                <div className='flex text-muted-foreground items-center'>
                    <HistoryLogo className='w-4 h-4 mr-1' />
                    <span>Recently used</span>
                </div>
                <Skeleton.Input active={true} />
            </>
        );
    }

    if (prompts.length === 0) {
        return null;
    }

    const promptsSet = new Set();
    const promptsToShow = [];
    prompts.forEach((p) => {
        if (promptsSet.has(p.promptId)) {
            return;
        }

        const foundPrompt = promptsTemplates.find((pt) => pt.id === p.promptId);
        if (foundPrompt) {
            promptsToShow.push(foundPrompt);
        }
        promptsSet.add(p.promptId);
    });

    const items = promptsToShow.map((t) => {
        return {
            key: t.id,
            label: `${t.emoji} ${t.title}`,
            onClick: () => {
                onSelect(t.id);
            },
        };
    });

    return (
        <>
            <div className='flex text-muted-foreground items-center'>
                <HistoryLogo className='w-4 h-4 mr-1' />
                <span>Recently used</span>
            </div>
            <Menu selectable={false} mode='inline' items={items.slice(0, 5)} />
        </>
    );
}

function PromptsDrawer({ isOpen, onClose, onSelect }) {
    const promptTemplates = useGetPromptsTemplates();
    const [query, setQuery] = useState('');
    const isEmptyQuery = !query;

    const popularIds = [
        'reply-chat',
        'mail-improve-draft',
        'mail-reply',
        'fix-grammar',
        'paraphrase',
    ];

    const handlePromptSelect = (promptId) => {
        onSelect(promptId);
        onClose();
    };
    const popularPrompts = promptTemplates
        .filter((p) => popularIds.includes(p.id))
        .map((t) => {
            return {
                key: t.id,
                label: `${t.emoji} ${t.title}`,
                onClick: () => {
                    handlePromptSelect(t.id);
                },
            };
        });

    const menuItems = promptTemplates
        .filter((p) => {
            if (isEmptyQuery) {
                return true;
            }

            return filterPromptsTemplates(query, p);
        })
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((t) => {
            return {
                key: t.id,
                label: `${t.emoji} ${t.title}`,
                onClick: () => {
                    handlePromptSelect(t.id);
                },
            };
        });

    return (
        <Drawer
            title='Select AI Prompt'
            placement={'bottom'}
            closable={false}
            onClose={onClose}
            open={isOpen}
            height={'90vh'}
            styles={{ body: { padding: '6px 12px 0 12px' } }}>
            <div className='flex flex-col gap-y-2 relative'>
                <div className='sticky top-0 bg-white z-10'>
                    <Input
                        size='large'
                        placeholder='Type to search'
                        allowClear={true}
                        prefix={<SearchOutlined />}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>
                {isEmptyQuery && (
                    <>
                        <CategoriesPrompts onSelect={handlePromptSelect} />
                        <RecentlyUsedPrompts onSelect={handlePromptSelect} />
                        <div className='flex text-muted-foreground items-center'>
                            <ChartNoAxesCombined className='w-4 h-4 mr-1' />
                            <span>Popular</span>
                        </div>
                        <Menu
                            selectable={false}
                            mode='inline'
                            items={popularPrompts}
                        />
                    </>
                )}
                <div className='flex text-muted-foreground items-center'>
                    <ArrowDownZA className='w-4 h-4 mr-1' />
                    <span>Templates</span>
                </div>
                <Menu selectable={false} mode='inline' items={menuItems} />
            </div>
        </Drawer>
    );
}

function NewRequest({
    selectedText,
    selectedPromptId,
    handleShowIntegrationsClick,
}: {
    selectedText?: string;
    selectedPromptId?: string;
    handleShowIntegrationsClick: () => void;
}) {
    const promptTemplates = useGetPromptsTemplates();
    const { isWord, isPowerPoint, isOutlook, isExcel } =
        useApplicationContext()!;
    const [showOutlookView, setShowOutlookView] = useState(isOutlook);

    const [searchParams, setSearchParams] = useSearchParams();
    const [currentState, setCurrentState] = useState('USER_INPUT_STATE');
    const [text, setText] = useState<string | undefined>(selectedText);

    function ShowInstructionButton() {
        if (!isWord) {
            return null;
        }

        return (
            <HoverCard>
                <HoverCardTrigger>
                    <Button variant='link' className='text-xs p-0 h-6 gap-1'>
                        Video Instruction
                        <span className='relative flex size-2'>
                            <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75'></span>
                            <span className='relative inline-flex size-2 rounded-full bg-red-500'></span>
                        </span>
                    </Button>
                </HoverCardTrigger>
                <HoverCardContent sideOffset={0} className='p-0 w-screen'>
                    <div
                        style={{
                            position: 'relative',
                            paddingTop: '72.37185056472633%',
                        }}>
                        <iframe
                            src='https://customer-b5iz2r6cpeptxyuk.cloudflarestream.com/0a8a672363dd4d47864553ce010f9773/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-b5iz2r6cpeptxyuk.cloudflarestream.com%2F0a8a672363dd4d47864553ce010f9773%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false'
                            style={{
                                border: 'none',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                width: '100%',
                            }}
                            allow='accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;'
                            allowFullScreen={true}></iframe>
                    </div>
                </HoverCardContent>
            </HoverCard>
        );
    }
    /*
    // Hook to get a full text of doc from Word
    const [documentText, setDocumentText] = useState('full doc');
    useEffect(() => {
        const Word = window.Word;

        if (!Word) {  
            return;
        }

        Word.run(function (context) {
            // Insert your code here. For example:
            const documentBody = context.document.body;
            context.load(documentBody);
            return context.sync().then(function () {
                setDocumentText(documentBody.text);
            });
        });
    }, []);
    */

    useEffect(() => {
        if (!isWord && !isPowerPoint) {
            return;
        }

        try {
            const Office = window.Office;
            function subscribeToEvent() {
                Office.context.document?.addHandlerAsync(
                    Office.EventType.DocumentSelectionChanged,
                    getSelection,
                );
            }

            function getSelection(evtArgs?: any) {
                const document = evtArgs
                    ? evtArgs.document
                    : Office.context.document;

                document?.getSelectedDataAsync(
                    Office.CoercionType.Text,
                    { valueFormat: 'unformatted' },
                    function (asyncResult: any) {
                        const error = asyncResult.error;
                        if (
                            asyncResult.status ===
                            Office.AsyncResultStatus.Failed
                        ) {
                            console.error(error.message);
                            return;
                        }

                        setText(asyncResult.value);
                    },
                );
            }
            subscribeToEvent();
            getSelection();
        } catch (err) {
            console.error(err);
        }
    }, [isPowerPoint, isWord]);

    useEffect(() => {
        if (!isExcel) {
            return;
        }

        try {
            const Office = window.Office;

            async function getSelectedFormula() {
                try {
                    await Excel.run(async (context) => {
                        let range = context.workbook.getSelectedRange();

                        // Load the formulas property
                        range.load('formulas');

                        await context.sync();

                        const formula = range.formulas[0][0] || '';
                        formula && setText(formula);
                    });
                } catch (error) {
                    console.error('Error getting formula:', error);
                }
            }

            Office.context.document.addHandlerAsync(
                Office.EventType.DocumentSelectionChanged,
                getSelectedFormula,
            );
        } catch (err) {
            console.error(err);
        }
    }, [isExcel]);

    const [lastPromptId, setPromptId] = useAtom(defaultPromptId);
    const handlePromptChange = useCallback(
        (promptId: string) => {
            setPromptId(promptId);
            if (selectedPromptId) {
                searchParams.delete('prompt');
                setSearchParams(searchParams);
            }
        },
        [setPromptId, setSearchParams, searchParams, selectedPromptId],
    );

    useEffect(() => {
        if (!selectedPromptId) {
            return;
        }

        handlePromptChange(selectedPromptId);
    }, [selectedPromptId]);

    const prompt = useMemo(() => {
        return lastPromptId
            ? promptTemplates.find((t) => t.id === lastPromptId)
            : undefined;
    }, [lastPromptId, promptTemplates]);

    const [open, setOpen] = useState(false);

    if (showOutlookView) {
        return (
            <OutlookRequest onChangeView={() => setShowOutlookView(false)} />
        );
    }

    return (
        <div className='w-full h-full flex flex-col overflow-y-auto'>
            <div className='w-full px-4 mb-2 flex flex-col flex-grow'>
                <div className='text-sm py-2 flex justify-between flex-nowrap'>
                    <span>AI Prompt Template</span>
                    <ShowInstructionButton />
                </div>
                <Button
                    variant={'outline'}
                    className='w-full px-4 justify-start'
                    disabled={currentState === 'FETCHING'}
                    size={'lg'}
                    onClick={() => setOpen(true)}>
                    <div className='flex w-full items-center justify-between'>
                        {prompt ? (
                            <div>
                                {prompt.emoji}&nbsp;{prompt.title}
                            </div>
                        ) : (
                            <div>👇 Click to select prompt template...</div>
                        )}
                        <Settings2 className='text-gray-400 !h-5 !w-5' />
                    </div>
                </Button>
                <PromptsDrawer
                    isOpen={open}
                    onClose={() => setOpen(false)}
                    onSelect={(prompt) => {
                        handlePromptChange(prompt);
                        setCurrentState('USER_INPUT_STATE');
                    }}
                />
            </div>
            {prompt ? (
                <AiPromptModalBody
                    currentState={currentState}
                    setCurrentState={setCurrentState}
                    autoSubmit={
                        !!selectedPromptId && !!selectedText ? true : false
                    }
                    aiTemplate={{ ...prompt, userPrompt: text }}
                    isFreeMode={false}
                    handleShowIntegrationsClick={handleShowIntegrationsClick}
                />
            ) : (
                <Placeholder />
            )}
        </div>
    );
}

function AskAiView() {
    const applicationContext = useApplicationContext()!;
    const account = applicationContext?.account;
    const { openDialog: openSignInDialog } = useSignInModal();
    const { openDialog: openSubscriptionPromotionDialog } =
        useSubscriptionPromotionModal();
    const { tokens, decreaseTokens } = useDisplayTokens();

    const onBeforeSubmit = useCallback(() => {
        if (!account) {
            openSignInDialog();
            return false;
        }
        if (!account?.isUnlimited && tokens <= 0) {
            openSubscriptionPromotionDialog();
            return false;
        }
        return true;
    }, [account, openSignInDialog, openSubscriptionPromotionDialog, tokens]);

    return (
        <ChatWidget
            onBeforeSubmit={onBeforeSubmit}
            onAfterSubmit={decreaseTokens}
        />
    );
}

// Dependencies: pnpm install lucide-react

import { ArrowRight } from 'lucide-react';

function Banner() {
    const { openDialog: openGetSubscriptionDialog } =
        useGetSubscriptionDialog();
    const applicationContext = useApplicationContext();
    const { tokens } = useDisplayTokens();

    const { account } = applicationContext!;

    if (!account || account.isUnlimited) {
        return null;
    }

    const label =
        tokens > 0
            ? `Only ${tokens} uses remaining! Unlock unlimited access by
                    subscribing today.`
            : 'Your free usage has ended. Don’t stop now — get a subscription to keep using the service without limits!';
    return (
        <div
            className='dark bg-muted px-4 py-3 text-foreground'
            onClick={openGetSubscriptionDialog}>
            <p className='flex justify-center text-sm'>
                <div className='group cursor-pointer'>
                    <span className='me-1 text-base leading-none'>✨</span>
                    {label}
                    <ArrowRight
                        className='-mt-0.5 ms-2 inline-flex opacity-60 transition-transform group-hover:translate-x-0.5'
                        size={16}
                        strokeWidth={2}
                        aria-hidden='true'
                    />
                </div>
            </p>
        </div>
    );
}

function AppTabs({
    selectedText = '',
    selectedPromptId = '',
    selectedTab = 'request',
    handleTabSelect,
}: {
    selectedText?: string;
    selectedPromptId?: string;
    selectedTab: string;
    handleTabSelect: (value: string) => void;
}) {
    const { openDialog: openAiFormDialog } = useAiPromptForm();

    return (
        <Tabs
            value={selectedTab}
            onValueChange={handleTabSelect}
            className='h-screen overflow-hidden w-full flex flex-col'>
            <Banner />
            <div>
                <ScrollArea>
                    <TabsList className='relative mb-3 h-auto w-full gap-0.5 bg-transparent p-0 before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-border'>
                        <TabsTrigger
                            value='request'
                            className='overflow-hidden rounded-b-none border-x border-t border-border bg-slate-50 py-2 data-[state=active]:z-10 data-[state=active]:shadow-none'>
                            <WandSparkles
                                className='-ms-0.5 me-1.5 opacity-60'
                                size={16}
                                strokeWidth={2}
                                aria-hidden='true'
                            />
                            <span>Request</span>
                        </TabsTrigger>
                        <TabsTrigger
                            value='requests-history'
                            className='overflow-hidden rounded-b-none border-x border-t border-border bg-slate-50 py-2 data-[state=active]:z-10 data-[state=active]:shadow-none'>
                            <History
                                className='-ms-0.5 me-1.5 opacity-60'
                                size={16}
                                strokeWidth={2}
                                aria-hidden='true'
                            />
                            <span>History</span>
                        </TabsTrigger>
                        <TabsTrigger
                            value='ask-ai'
                            className='text-[14px] overflow-hidden rounded-b-none border-x border-t border-border bg-slate-50 py-2 data-[state=active]:z-10 data-[state=active]:shadow-none'>
                            <BotMessageSquare
                                className='-ms-0.5 me-1.5 opacity-60'
                                size={16}
                                strokeWidth={2}
                                aria-hidden='true'
                            />
                            <span>Ask AI</span>
                        </TabsTrigger>
                        <TabsTrigger
                            value='account-details'
                            className='overflow-hidden rounded-b-none border-x border-t border-border bg-slate-50 py-2 data-[state=active]:z-10 data-[state=active]:shadow-none'>
                            <CircleUserRound
                                className='-ms-0.5 me-1.5 opacity-60'
                                size={16}
                                strokeWidth={2}
                                aria-hidden='true'
                            />
                            <span>Account</span>
                        </TabsTrigger>
                        <TabsTrigger
                            value='integrations-list'
                            className='overflow-hidden rounded-b-none border-x border-t border-border bg-slate-50 py-2 data-[state=active]:z-10 data-[state=active]:shadow-none'>
                            <Unplug
                                className='-ms-0.5 me-1.5 opacity-60'
                                size={16}
                                strokeWidth={2}
                                aria-hidden='true'
                            />
                            <span>Integrations</span>
                        </TabsTrigger>
                    </TabsList>
                    <ScrollBar orientation='horizontal' />
                </ScrollArea>
            </div>
            <div className='flex-grow overflow-hidden'>
                <TabsContent value='request' className='w-full h-full'>
                    <NewRequest
                        selectedText={selectedText}
                        selectedPromptId={selectedPromptId}
                        handleShowIntegrationsClick={() =>
                            handleTabSelect('integrations-list')
                        }
                    />
                </TabsContent>
                <TabsContent value='requests-history' className='w-full h-full'>
                    <PromptsHistoryList onClick={openAiFormDialog} />
                </TabsContent>
                <TabsContent value='ask-ai' className='w-full h-full'>
                    <AskAiView />
                </TabsContent>
                <TabsContent value='account-details' className='w-full h-full'>
                    <AccountDetails
                        handleShowIntegrationsClick={() =>
                            handleTabSelect('integrations-list')
                        }
                    />
                </TabsContent>
                <TabsContent
                    value='integrations-list'
                    className='w-full h-full'>
                    <IntegrationsList />
                </TabsContent>
            </div>
        </Tabs>
    );
}

import { LogOut } from 'lucide-react';
import { useSignOutWithMsal } from '../Header';

function SignOutButton() {
    const handleSignOut = useSignOutWithMsal();
    return (
        <Button
            variant='outline'
            onClick={handleSignOut}
            className='min-w-[150px]'>
            <LogOut
                className='me-1 opacity-60'
                size={16}
                strokeWidth={2}
                aria-hidden='true'
            />
            Sign Out
        </Button>
    );
}

function AccountSegment({ account }: { account: any }) {
    const applicationContext = useApplicationContext();
    function renderAccountInformation() {
        if (!account) {
            return (
                <Link to={applicationContext!.loginUrl}>
                    <Button size={'lg'} className='min-w-[150px]'>
                        Sign In
                    </Button>
                </Link>
            );
        }

        return (
            <div className='flex flex-col gap-y-2'>
                <span className='text-sm font-normal'>
                    Email: {account.email}
                </span>
                <SignOutButton />
            </div>
        );
    }

    return (
        <div className='border-b py-4'>
            <span className='flex items-center gap-3'>
                <span
                    className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border'
                    aria-hidden='true'>
                    <CircleUserRound
                        size={16}
                        strokeWidth={2}
                        className='opacity-60'
                    />
                </span>
                <span className='flex flex-col space-y-1'>
                    <span className='font-semibold'>Account</span>
                    <span className='text-sm font-normal text-muted-foreground'>
                        Information about your account
                    </span>
                    {renderAccountInformation()}
                </span>
            </span>
        </div>
    );
}

function ExternalLinkButton({
    children,
    url,
}: {
    children: string;
    url: string;
}) {
    return (
        <Button
            asChild
            variant='outline'
            className='min-w-[150px] text-xs sm:text-sm'>
            <Link to={url} target='_blank' rel='noopener noreferrer'>
                {children}
                <SquareArrowOutUpRight
                    className='-me-1 ms-2 opacity-60 hidden sm:inline-block'
                    size={16}
                    strokeWidth={2}
                    aria-hidden='true'
                />
            </Link>
        </Button>
    );
}
function CancelSubscriptionButton() {
    return (
        <ExternalLinkButton url='https://ipirozhenko.onfastspring.com/ai-assistant/account'>
            Cancel Subscription
        </ExternalLinkButton>
    );
}

function SubmitSupportRequestButton() {
    return (
        <ExternalLinkButton url='https://help.perfectassistant.ai/feedback'>
            Submit Support Request
        </ExternalLinkButton>
    );
}

function HelpCenterButton() {
    return (
        <ExternalLinkButton url='https://help.perfectassistant.ai/'>
            Open Help Center
        </ExternalLinkButton>
    );
}

function BrowseIntegrationsButton({ onClick }: { onClick: () => void }) {
    return (
        <Button
            variant='outline'
            className='min-w-[150px] text-xs sm:text-sm'
            onClick={onClick}>
            Browse Integrations
        </Button>
    );
}

import { SquareArrowOutUpRight } from 'lucide-react';

import { cn } from '~/lib/utils';
import { useGetSubscriptionDialog } from '../GetSubscriptionModal';
import IconRipple from '../RippleIcon';
import AiButton from '../AiButton';
import { useDebouncedCallback } from 'use-debounce';
import copy from 'copy-to-clipboard';
import { BellOutlined, SearchOutlined } from '@ant-design/icons';

export function PurchaseSubscriptionButton() {
    const { openDialog: openGetSubscriptionDialog } =
        useGetSubscriptionDialog();

    const label = 'Purchase Subscription';
    return (
        <button
            className='group cursor-pointer rounded-xl border-4 border-violet-800 border-opacity-0 bg-transparent p-1 transition-all duration-500 hover:border-opacity-100'
            onClick={openGetSubscriptionDialog}>
            <div className='relative flex items-center justify-center gap-4 overflow-hidden rounded-lg bg-violet-800 px-6 py-4 font-bold text-white'>
                {label}
                <div
                    className={cn(
                        'absolute -left-16 top-0 h-full w-12 rotate-[30deg] scale-y-150 bg-white/10 transition-all duration-700 group-hover:left-[calc(100%+1rem)]',
                    )}
                />
            </div>
        </button>
    );
}

function ActivateSubscriptionButton() {
    const { openDialog: openActivateSubscriptionDialog } =
        useActivateSubscriptionDialog();
    return (
        <Button
            onClick={openActivateSubscriptionDialog}
            className='min-w-[150px] text-xs sm:text-sm'
            variant={'outline'}>
            Activate Subscription
        </Button>
    );
}

function GetReferralCodeSegment({ account }) {
    const messageApi = useMessagesAPI();

    if (!account) {
        return null;
    }

    const refCode = account.refCode.toUpperCase();
    function handleCopyToClipboard(str: string) {
        if (!str) {
            return;
        }
        copy(str, { format: 'text/plain' });
        messageApi?.info('Copied to clipboard');
    }

    return (
        <div className='border-b py-4'>
            <span className='flex items-center gap-3'>
                <span
                    className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border'
                    aria-hidden='true'>
                    <Users size={16} strokeWidth={2} className='opacity-60' />
                </span>
                <span className='flex flex-col space-y-1'>
                    <span className='font-semibold'>Your Referral Code</span>
                    <span className='text-sm font-normal text-muted-foreground'>
                        Get <b>100 tokens</b> for each friend you invite
                    </span>
                    <div className='flex w-full max-w-sm items-center gap-2'>
                        <ShadcnInput
                            type='text'
                            placeholder='You referral code'
                            readOnly
                            value={refCode}
                        />
                        <Button
                            type='submit'
                            variant='outline'
                            onClick={() => handleCopyToClipboard(refCode)}>
                            Copy
                        </Button>
                    </div>
                </span>
            </span>
        </div>
    );
}

function ActivateReferralSegment({ account }) {
    const fetcher = useFetcher();
    const data = fetcher.data;
    const state = fetcher.state;

    useEffect(() => {
        if (state === 'idle' && data?.isError === false) {
            window.location.reload();
        }
    }, [state, data]);

    if (!account || account.isUnlimited) {
        return null;
    }

    return (
        <div className='border-b py-4'>
            <span className='flex items-center gap-3'>
                <span
                    className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border'
                    aria-hidden='true'>
                    <Gem size={16} strokeWidth={2} className='opacity-60' />
                </span>
                <span className='flex flex-col space-y-1'>
                    <span className='font-semibold'>
                        Activate Referral Code
                    </span>
                    <span className='text-sm font-normal text-muted-foreground'>
                        Activate Your Friend’s Referral Code to get <b>100</b>{' '}
                        Free Tokens
                    </span>
                    {account.refBonusBy ? (
                        <div>
                            🎉&nbsp;<b>100 Free Tokens</b>Successfully Applied
                            to your account
                        </div>
                    ) : (
                        <fetcher.Form
                            method='post'
                            className='flex w-full max-w-sm items-center gap-2'
                            action='/referral'>
                            <ShadcnInput
                                name='code'
                                type='text'
                                disabled={fetcher.state !== 'idle'}
                                placeholder='Enter Invite Code'
                            />
                            <Button
                                type='submit'
                                variant='outline'
                                disabled={fetcher.state !== 'idle'}>
                                {fetcher.state !== 'idle'
                                    ? 'Activating...'
                                    : 'Activate'}
                            </Button>
                        </fetcher.Form>
                    )}
                    {fetcher.state === 'idle' && fetcher.data?.isError && (
                        <div className='w-full text-center'>
                            <p className='text-red-500'>
                                Failed to activate referral code
                            </p>
                        </div>
                    )}
                </span>
            </span>
        </div>
    );
}

function SubscriptionSegment({ account, tokens }) {
    if (!account) {
        return null;
    }

    function renderSubscriptionInformation() {
        const isFreePlan = !account.isUnlimited;
        if (isFreePlan) {
            return (
                <div className='flex flex-col  gap-y-2'>
                    <span className='text-sm font-normal'>Plan: FREE</span>
                    <span className='text-sm font-normal'>
                        Tokens Left: {tokens}
                    </span>
                    <PurchaseSubscriptionButton />
                    <ActivateSubscriptionButton />
                </div>
            );
        } else {
            return (
                <div className='flex flex-col  gap-y-2'>
                    <span className='text-sm font-normal'>
                        Plan: Paid Subscription
                    </span>
                    <CancelSubscriptionButton />
                </div>
            );
        }
    }

    return (
        <div className='border-b py-4'>
            <span className='flex items-center gap-3'>
                <span
                    className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border'
                    aria-hidden='true'>
                    <CircleDollarSign
                        size={16}
                        strokeWidth={2}
                        className='opacity-60'
                    />
                </span>
                <span className='flex flex-col space-y-1'>
                    <span className='font-semibold'>Subscription</span>
                    <span className='text-sm font-normal text-muted-foreground'>
                        Information about your subscription
                    </span>
                    {renderSubscriptionInformation()}
                </span>
            </span>
        </div>
    );
}

function IntegrationsSection({
    handleShowIntegrationsClick,
}: {
    handleShowIntegrationsClick: () => void;
}) {
    return (
        <div className='border-b py-4'>
            <span className='flex items-center gap-3'>
                <span
                    className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border'
                    aria-hidden='true'>
                    <Unplug size={16} strokeWidth={2} className='opacity-60' />
                </span>
                <span className='flex flex-col space-y-1'>
                    <span className='font-semibold'>Integrations</span>
                    <span className='text-sm font-normal text-muted-foreground'>
                        We integrated with Outlook, Word, PowerPoint, Telegram,
                        Gmail, Chrome, and more!
                    </span>
                    <div>
                        <BrowseIntegrationsButton
                            onClick={handleShowIntegrationsClick}
                        />
                    </div>
                </span>
            </span>
        </div>
    );
}

function WhatsNewSection() {
    return (
        <div className='border-b py-4'>
            <span className='flex items-center gap-3'>
                <span
                    className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border'
                    aria-hidden='true'>
                    <BellOutlined
                        rotate={45}
                        size={16}
                        className='opacity-60'
                    />
                </span>
                <span className='flex flex-col space-y-1'>
                    <span className='font-semibold'>What's New</span>
                    <span className='text-sm font-normal text-muted-foreground'>
                        Discover the exciting new features we've just rolled out
                    </span>
                    <div className='flex flex-col  gap-y-2'>
                        <ExternalLinkButton url='/whats-new'>
                            See What's New
                        </ExternalLinkButton>
                    </div>
                </span>
            </span>
        </div>
    );
}

function ContactSupportSection() {
    return (
        <>
            <div className='border-b py-4'>
                <span className='flex items-center gap-3'>
                    <span
                        className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border'
                        aria-hidden='true'>
                        <LifeBuoy
                            size={16}
                            strokeWidth={2}
                            className='opacity-60'
                        />
                    </span>
                    <span className='flex flex-col space-y-1'>
                        <span className='font-semibold'>Help Center</span>
                        <span className='text-sm font-normal text-muted-foreground'>
                            Self-serve help center working 24/7
                        </span>
                        <div className='flex flex-col gap-y-2'>
                            <HelpCenterButton />
                        </div>
                    </span>
                </span>
            </div>
            <div className='border-b py-4'>
                <span className='flex items-center gap-3'>
                    <span
                        className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border'
                        aria-hidden='true'>
                        <LifeBuoy
                            size={16}
                            strokeWidth={2}
                            className='opacity-60'
                        />
                    </span>
                    <span className='flex flex-col space-y-1'>
                        <span className='font-semibold'>Contact Support</span>
                        <span className='text-sm font-normal text-muted-foreground'>
                            We're here to help 24/7
                        </span>
                        <div className='flex flex-col  gap-y-2'>
                            <SubmitSupportRequestButton />
                        </div>
                    </span>
                </span>
            </div>
        </>
    );
}

function ShowGuideButtonRow({
    children,
    name,
    imgUrl,
    supportBtnText = 'Request support',
    supportBtnUrl = 'https://help.perfectassistant.ai/feedback',
}: {
    children: React.ReactNode;
    name: string;
    imgUrl: string;
    supportBtnText?: string;
    supportBtnUrl?: string;
}) {
    return (
        <Dialog>
            <DialogTrigger asChild>
                <TableRow className='hover:bg-muted/50 cursor-pointer'>
                    <TableCell>
                        <img
                            alt={`${name} logo`}
                            src={imgUrl}
                            className='mx-auto w-8 h-8'
                        />
                    </TableCell>
                    <TableCell>{name}</TableCell>
                    <TableCell className='text-right'>
                        <Button variant={'outline'} size='sm'>
                            <span className='sr-only sm:not-sr-only'>
                                Install
                            </span>
                            <ArrowUpRight />
                        </Button>
                    </TableCell>
                </TableRow>
            </DialogTrigger>
            <DialogContent className='overflow-y-auto max-h-screen'>
                <DialogHeader>
                    <p className='flex items-center text-muted-foreground'>
                        <CircleHelp size={18} className='mr-2' />
                        Installation Guide
                    </p>
                </DialogHeader>
                <div className='flex flex-col'>
                    {children}

                    <p className='p-2 text-sm text-muted-foreground text-center'>
                        Can’t install the add-in?{' '}
                        <Button
                            asChild
                            size='sm'
                            variant={'link'}
                            className='underline'>
                            <a
                                href={supportBtnUrl}
                                target='_blank'
                                rel='noopener noreferrer'>
                                {supportBtnText}
                                <ExternalLink
                                    size={12}
                                    className='ml-1 inline-block'
                                />
                            </a>
                        </Button>
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function LinkIntegration({ url, name, iconUrl }) {
    return (
        <TableRow
            className='hover:bg-muted/50 cursor-pointer'
            onClick={() => window.open(url, '_blank')}>
            <TableCell>
                <img
                    alt={`${name} logo`}
                    src={iconUrl}
                    className='mx-auto w-8 h-8'
                />
            </TableCell>
            <TableCell>{name}</TableCell>
            <TableCell className='text-right'>
                <Button size='sm' variant={'outline'}>
                    <span className='sr-only sm:not-sr-only'>Install</span>
                    <ArrowUpRight />
                </Button>
            </TableCell>
        </TableRow>
    );
}

export function IntegrationsList() {
    return (
        <div className='w-full h-full overflow-y-auto overflow-x-hidden'>
            <Table>
                <TableCaption className='p-2'>
                    Didn’t find the sought for integration?
                    <Button variant={'link'} asChild className='underline'>
                        <a
                            href='https://help.perfectassistant.ai/feedback'
                            target='_blank'
                            rel='noopener noreferrer'>
                            Submit a request to add{' '}
                            <ExternalLink size={12} className='inline-block' />
                        </a>
                    </Button>
                </TableCaption>
                <TableBody>
                    <TableRow
                        className='hover:bg-muted/50 cursor-pointer'
                        onClick={() => window.open('/iframe', '_blank')}>
                        <TableCell>
                            <Globe className='mx-auto w-8 h-8' />
                        </TableCell>
                        <TableCell>Web Version</TableCell>
                        <TableCell className='text-right'>
                            <Button size='sm'>
                                <span className='sr-only sm:not-sr-only'>
                                    Open
                                </span>
                                <ArrowUpRight />
                            </Button>
                        </TableCell>
                    </TableRow>
                    <ShowGuideButtonRow
                        name='Microsoft Outlook'
                        imgUrl={OutlookLogo}
                        supportBtnText='Sideload add-in'
                        supportBtnUrl='https://help.perfectassistant.ai/How-to-Install-AI-Perfect-Assistant-Add-In-to-Outlook_qNpdkGuIlSHxPeiHixHc'>
                        <iframe
                            className='w-full aspect-video max-w-screen-lg '
                            src='https://www.youtube-nocookie.com/embed/rFFVExX6z5g?si=dq0m-3Diq1IMrhZo'
                            title='YouTube video player'
                            frameBorder='0'
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                            referrerPolicy='strict-origin-when-cross-origin'
                            allowFullScreen></iframe>
                        <ol className='list-decimal p-4 space-y-2'>
                            <li>
                                <b>Open Outlook:</b> Start by opening your
                                Microsoft Outlook application.
                                <div className='my-2'>
                                    <Button asChild size='sm'>
                                        <a
                                            href='https://outlook.office.com/mail/'
                                            target='_blank'>
                                            Open Outlook
                                            <ArrowUpRight />
                                        </a>
                                    </Button>
                                </div>
                            </li>
                            <li>
                                <b>Go to the Add-ins Store:</b> Click on the
                                "File" tab at the top left corner of your
                                Outlook window. In the sidebar that opens, click
                                on "Manage Add-Ins".
                            </li>
                            <li>
                                <b>Install the Add-in:</b> In the Add-Ins Store,
                                use the search bar to find the{' '}
                                <b>"AI Perfect Assistant for Outlook"</b>. Once
                                you find the add-in, click Add or Install next
                                to its listing.
                            </li>
                        </ol>
                    </ShowGuideButtonRow>
                    <ShowGuideButtonRow name='Microsoft Word' imgUrl={WordLogo}>
                        <iframe
                            className='w-full aspect-video max-w-screen-lg'
                            src='https://www.youtube-nocookie.com/embed/DOzALcGJjzw?si=epJu2E6_ev_l3M_s'
                            title='YouTube video player'
                            frameBorder='0'
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                            referrerPolicy='strict-origin-when-cross-origin'
                            allowFullScreen></iframe>
                        <div className='flex gap-2 my-2 mx-auto'>
                            <Button asChild size='sm'>
                                <a
                                    href='/chatgpt-for-microsoft-word/install/desktop'
                                    target='_blank'>
                                    Install for Word App
                                    <ArrowUpRight />
                                </a>
                            </Button>
                            <Button asChild size='sm'>
                                <a
                                    href='/chatgpt-for-microsoft-word/install/online'
                                    target='_blank'>
                                    Install for Word Online
                                    <ArrowUpRight />
                                </a>
                            </Button>
                        </div>
                        <div className='flex gap-2 my-2 mx-auto'>
                            <Button asChild variant='outline' size='sm'>
                                <a
                                    href='/downloads/AiPerfectAssistant.zip'
                                    download>
                                    Download (.EXE) installer for Windows
                                    <ArrowUpRight />
                                </a>
                            </Button>
                        </div>
                        <ol className='list-decimal p-4 space-y-2'>
                            <li>
                                <b>Open Microsoft Word:</b> Start by opening
                                your Microsoft Word application. Create or open
                                any file.
                            </li>
                            <li>
                                <b>Go to the Add-ins Store:</b> Click on the
                                "Home" tab at the top left corner of your Word
                                window. On the toolbar that opens, click on
                                "Add-Ins" button. In the opened popup click "+
                                More Add-ins" button.
                            </li>
                            <li>
                                <b>Install the Add-in:</b> In the Add-Ins Store,
                                use the search bar to find the{' '}
                                <b>"AI Perfect Assistant for Office"</b>. Once
                                you find the add-in, click Add or Install next
                                to its listing.
                            </li>
                        </ol>
                    </ShowGuideButtonRow>
                    <LinkIntegration
                        name='Microsoft Teams'
                        iconUrl={TeamsLogo}
                        url='/integrations/teams'
                    />
                    <ShowGuideButtonRow
                        name='Microsoft PowerPoint'
                        imgUrl={PowerPointLogo}>
                        <iframe
                            className='w-full aspect-video max-w-screen-lg '
                            src='https://www.youtube-nocookie.com/embed/fq0UQ9oMyck?si=Icf_DDLbgFJhicHp'
                            title='YouTube video player'
                            frameBorder='0'
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                            referrerPolicy='strict-origin-when-cross-origin'
                            allowFullScreen></iframe>
                        <div className='flex gap-2 my-2 mx-auto'>
                            <Button asChild size='sm'>
                                <a
                                    href='/chatgpt-for-microsoft-powerpoint/install/desktop'
                                    target='_blank'>
                                    Install for PowerPoint App
                                    <ArrowUpRight />
                                </a>
                            </Button>
                            <Button asChild size='sm'>
                                <a
                                    href='/chatgpt-for-microsoft-powerpoint/install/online'
                                    target='_blank'>
                                    Install for PowerPoint Online
                                    <ArrowUpRight />
                                </a>
                            </Button>
                        </div>
                        <div className='flex gap-2 my-2 mx-auto'>
                            <Button asChild variant='outline' size='sm'>
                                <a
                                    href='/downloads/AiPerfectAssistant.zip'
                                    download>
                                    Download (.EXE) installer for Windows
                                    <ArrowUpRight />
                                </a>
                            </Button>
                        </div>
                        <ol className='list-decimal p-4 space-y-2'>
                            <li>
                                <b>Open PowerPoint:</b> Start by opening
                                PowerPoint application. Create or open any file.
                            </li>
                            <li>
                                <b>Go to the Add-ins Store:</b> Click on the
                                "Home" tab at the top left corner of PowerPoint
                                window. On the toolbar that opens, click on
                                "Add-Ins" button. In the opened popup click "+
                                More Add-ins" button.
                            </li>
                            <li>
                                <b>Install the Add-in:</b> In the Add-Ins Store,
                                use the search bar to find the{' '}
                                <b>"AI Perfect Assistant for Office"</b>. Once
                                you find the add-in, click Add or Install next
                                to its listing.
                            </li>
                        </ol>
                    </ShowGuideButtonRow>
                    <ShowGuideButtonRow
                        name='Microsoft Excel'
                        imgUrl={ExcelLogo}>
                        <img
                            alt={`Excel logo`}
                            src={ExcelLogo}
                            className='mx-auto w-16'
                        />
                        <div className='flex gap-2 my-2 mx-auto'>
                            <Button asChild size='sm'>
                                <a
                                    href='/chatgpt-for-microsoft-excel/install/desktop'
                                    target='_blank'>
                                    Install for Excel App
                                    <ArrowUpRight />
                                </a>
                            </Button>
                            <Button asChild size='sm'>
                                <a
                                    href='/chatgpt-for-microsoft-excel/install/online'
                                    target='_blank'>
                                    Install for Excel Online
                                    <ArrowUpRight />
                                </a>
                            </Button>
                        </div>
                        <div className='flex gap-2 my-2 mx-auto'>
                            <Button asChild variant='outline' size='sm'>
                                <a
                                    href='/downloads/AiPerfectAssistant.zip'
                                    download>
                                    Download (.EXE) installer for Windows
                                    <ArrowUpRight />
                                </a>
                            </Button>
                        </div>
                        <ol className='list-decimal p-4 space-y-2'>
                            <li>
                                <b>Open Excel:</b> Start by opening Excel
                                application. Create or open any file.
                            </li>
                            <li>
                                <b>Go to the Add-ins Store:</b> Click on the
                                "Home" tab at the top left corner of Excel
                                window. On the toolbar that opens, click on
                                "Add-Ins" button. In the opened popup click "+
                                More Add-ins" button.
                            </li>
                            <li>
                                <b>Install the Add-in:</b> In the Add-Ins Store,
                                use the search bar to find the{' '}
                                <b>"AI Perfect Assistant for Office"</b>. Once
                                you find the add-in, click Add or Install next
                                to its listing.
                            </li>
                        </ol>
                    </ShowGuideButtonRow>
                    <ShowGuideButtonRow
                        name='Microsoft OneNote'
                        imgUrl={OneNote}>
                        <img
                            alt='Microsoft OneNote logo'
                            src={OneNote}
                            className='mx-auto w-16'
                        />
                        <ol className='list-decimal p-4 space-y-2'>
                            <li>
                                <b>Open OneNote:</b> Start by opening OneNote
                                application. Create or open any file.
                                <div className='my-2'>
                                    <Button asChild size='sm'>
                                        <a
                                            href='https://www.onenote.com/notebooks'
                                            target='_blank'>
                                            Open OneNote
                                            <ArrowUpRight />
                                        </a>
                                    </Button>
                                </div>
                            </li>
                            <li>
                                <b>Go to the Add-ins Store:</b> Click on the
                                "Home" tab at the top left corner of OneNote
                                window. On the toolbar that opens, click on
                                "Add-Ins" button. In the opened popup click "+
                                More Add-ins" button.
                            </li>
                            <li>
                                <b>Install the Add-in:</b> In the Add-Ins Store,
                                use the search bar to find the{' '}
                                <b>"AI Perfect Assistant for Office"</b>. Once
                                you find the add-in, click Add or Install next
                                to its listing.
                            </li>
                        </ol>
                    </ShowGuideButtonRow>
                    <LinkIntegration
                        name='Google Docs'
                        iconUrl={DocsLogo}
                        url='/integrations/google-workspace'
                    />
                    <LinkIntegration
                        name='Google Sheets'
                        iconUrl={SheetsLogo}
                        url='/integrations/google-workspace'
                    />
                    <LinkIntegration
                        name='Google Slides'
                        iconUrl={SlidesLogo}
                        url='/integrations/google-workspace'
                    />
                    <LinkIntegration
                        name='Gmail'
                        iconUrl={GmailLogo}
                        url='/integrations/google-chrome'
                    />
                    <LinkIntegration
                        name='Chrome'
                        iconUrl={ChromeLogo}
                        url='/integrations/google-chrome'
                    />
                    <LinkIntegration
                        name='Telegram'
                        iconUrl={TelegramLogo}
                        url='/integrations/telegram'
                    />
                    <LinkIntegration
                        name='Windows App'
                        iconUrl={WindowsMarketplaceLogo}
                        url='/integrations/windows-marketplace'
                    />
                    <ShowGuideButtonRow name='iPhone & iPad' imgUrl={AppleLogo}>
                        <QRCode value={'https://perfectassistant.ai/iframe'} />
                        <ol className='list-decimal p-4 space-y-2'>
                            <li>
                                <b>Open AI Assistant in Safari:</b> Scan QR code
                                above or launch the Safari browser on your
                                iPhone or iPad. Navigate to the following
                                URL:&nbsp;
                                <Link
                                    to='https://perfectassistant.ai/iframe'
                                    className='underline'
                                    target='_blank'>
                                    https://perfectassistant.ai/iframe
                                </Link>
                            </li>
                            <li>
                                <b>Tap the Share Icon:</b>&nbsp;Once the page
                                loads, tap the Share button. <Share />
                            </li>
                            <li>
                                <b>Select "Add to Home Screen":</b> Scroll down
                                the sharing options until you find "Add to Home
                                Screen". Tap it. The app icon will now appear on
                                your home screen.
                            </li>
                        </ol>
                    </ShowGuideButtonRow>
                    <ShowGuideButtonRow name='Android' imgUrl={AndroidLogo}>
                        <QRCode value={'https://perfectassistant.ai/iframe'} />
                        <ol className='list-decimal p-4 space-y-2'>
                            <li>
                                <b>
                                    Open AI Assistant in Chrome (or another
                                    supported browser):
                                </b>{' '}
                                Scan QR code above or launch the browser on your
                                phone. Navigate to the following URL:&nbsp;
                                <Link
                                    to='https://perfectassistant.ai/iframe'
                                    className='underline'
                                    target='_blank'>
                                    https://perfectassistant.ai/iframe
                                </Link>
                            </li>
                            <li>
                                <b>Tap the three-dot menu (⁝):</b>
                                &nbsp;Once the page loads, Tap the three-dot
                                menu (⁝) in the top-right corner.
                            </li>
                            <li>
                                <b>Select "Add to Home Screen":</b> A pop-up
                                will appear asking if you want to install the
                                app. Tap "Install" or "Add" to proceed. The app
                                icon will now appear on your home screen like a
                                regular app.
                            </li>
                        </ol>
                    </ShowGuideButtonRow>
                </TableBody>
            </Table>
        </div>
    );
}

function AccountDetails({
    handleShowIntegrationsClick,
}: {
    handleShowIntegrationsClick: () => void;
}) {
    const applicationContext = useApplicationContext();
    const { tokens } = useDisplayTokens();

    const { account, isWord, isOutlook, isPowerPoint, isIframe } =
        applicationContext!;

    const couldPerformSignInOrSignOut =
        isWord || isOutlook || isPowerPoint || isIframe;

    return (
        <div className='p-2 h-full overflow-y-auto'>
            <AccountSegment
                account={account}
                couldPerformSignInOrSignOut={couldPerformSignInOrSignOut}
                tokens={tokens}
            />
            <SubscriptionSegment account={account} tokens={tokens} />
            <ActivateReferralSegment account={account} />
            <GetReferralCodeSegment account={account} />
            <IntegrationsSection
                handleShowIntegrationsClick={handleShowIntegrationsClick}
            />
            <WhatsNewSection />
            <ContactSupportSection />
        </div>
    );
}

export default function AiGrid() {
    const { account } = useApplicationContext() as any;
    const [currentSegment, setSegment] = useAtom(defaultSegment);
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedText = searchParams.get('input');
    const selectedPromptId = searchParams.get('prompt');

    const isUnlimited = account?.isUnlimited || false;
    const handleTabChange = useCallback(
        (tabId: string) => {
            setSegment(tabId);

            if (tabId !== 'request') {
                /* IF WE CHANGING TAB AFTER AUTOSUBMIT ->
           WE REMOVE URL PARAMS TO AVOID RUN OF AUTOSUBMIT */
                searchParams.delete('prompt');
                searchParams.delete('input');
                setSearchParams(searchParams);
            }
        },
        [setSegment],
    );

    useEffect(() => {
        if (selectedText) {
            handleTabChange('request');
            return;
        }
    }, [selectedText, setSegment]);

    const [messageApi, contextHolder] = message.useMessage();
    useSetMessagesAPI(messageApi);

    return (
        <div className='w-full h-full'>
            <AppTabs
                selectedTab={currentSegment}
                handleTabSelect={handleTabChange}
                selectedText={selectedText || undefined}
                selectedPromptId={selectedPromptId || undefined}
            />
            <AiPromptForm />
            <SignInModal />
            <ActivateSubscriptionModal />
            <GetSubscriptionModal />
            <SubscriptionPromotionModal
                account={account}
                navigateToAccountTab={() => handleTabChange('account-details')}
            />
            {isUnlimited && <div className='hidden posthog-paid-account'></div>}
            {contextHolder}
        </div>
    );
}
